import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';

const ProjectCollaborators = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [totalPourcentage, setTotalPourcentage] = useState(0);
  const [canManage, setCanManage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({
    pourcentage_benefices: '',
    salaire_fixe: '',
    role: 'associe',
    notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Récupérer les membres de l'équipe du projet ET les données de collaborateurs financiers
      const [teamRes, collabRes, projectRes] = await Promise.all([
        axios.get(`${API_URL}/projects/${projectId}/team`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/project-finances/${projectId}/collaborators?include_inactive=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const team = teamRes.data || [];
      const collabs = collabRes.data.collaborators || [];
      const proj = projectRes.data.project || projectRes.data;
      
      console.log('API Response - can_manage:', collabRes.data.can_manage);
      console.log('Full collabRes.data:', collabRes.data);
      setCanManage(collabRes.data.can_manage || false);
      
      // Calculer le total des pourcentages attribués
      const total = collabs.reduce((sum, c) => sum + (parseFloat(c.pourcentage_benefices) || 0), 0);
      setTotalPourcentage(total);
      
      // Fusionner les données : membres de l'équipe avec leurs infos financières
      const mergedMembers = team.map(member => {
        const memberId = member.user_id || member.id;
        const collab = collabs.find(c => c.user_id === memberId);
        return {
          ...member,
          user_id: memberId,
          collaborator_id: collab?.id || null,
          pourcentage_benefices: collab?.pourcentage_benefices || 0,
          salaire_fixe: collab?.salaire_fixe || 0,
          role_finance: collab?.role || 'associe',
          role_projet: member.task_type || collab?.task_type || member.role_projet || member.role || null,
          statut_finance: collab?.statut || 'actif',
          total_paye: collab?.total_paye || 0,
          notes: collab?.notes || '',
          date_debut: collab?.date_debut || null
        };
      });
      
      // Ajouter les collaborateurs qui ne sont pas dans l'équipe (ajoutés manuellement)
      collabs.forEach(collab => {
        const existsInTeam = mergedMembers.find(m => m.user_id === collab.user_id);
        if (!existsInTeam && collab.user_id !== proj?.author_id) {
          mergedMembers.push({
            id: collab.user_id,
            user_id: collab.user_id,
            nom: collab.nom,
            email: collab.email,
            collaborator_id: collab.id,
            pourcentage_benefices: collab.pourcentage_benefices || 0,
            salaire_fixe: collab.salaire_fixe || 0,
            role_finance: collab.role || 'associe',
            role_projet: collab.task_type || null,
            statut_finance: collab.statut || 'actif',
            total_paye: collab.total_paye || 0,
            notes: collab.notes || '',
            date_debut: collab.date_debut || null
          });
        }
      });
      
      // Ajouter l'auteur du projet en premier s'il n'est pas déjà dans l'équipe
      if (proj?.author_id) {
        const authorInTeam = mergedMembers.find(m => (m.user_id || m.id) === proj.author_id);
        if (!authorInTeam) {
          const authorCollab = collabs.find(c => c.user_id === proj.author_id);
          mergedMembers.unshift({
            id: proj.author_id,
            user_id: proj.author_id,
            nom: proj.author_nom || 'Auteur',
            email: proj.author_email || '',
            role: 'Auteur du projet',
            is_author: true,
            collaborator_id: authorCollab?.id || null,
            pourcentage_benefices: 0, // L'auteur garde le reste
            salaire_fixe: authorCollab?.salaire_fixe || 0,
            role_finance: 'auteur',
            statut_finance: 'actif',
            total_paye: authorCollab?.total_paye || 0
          });
        } else {
          // Marquer l'auteur existant
          const idx = mergedMembers.findIndex(m => (m.user_id || m.id) === proj.author_id);
          if (idx !== -1) {
            mergedMembers[idx].is_author = true;
            mergedMembers[idx].role_finance = 'auteur';
          }
        }
      }
      
      setTeamMembers(mergedMembers);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditParts = (member) => {
    setEditingMember(member);
    setFormData({
      pourcentage_benefices: member.pourcentage_benefices || '',
      salaire_fixe: member.salaire_fixe || '',
      role: member.role_finance || 'associe',
      notes: member.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingMember.collaborator_id) {
        // Mise à jour du collaborateur existant
        await axios.put(
          `${API_URL}/project-finances/${projectId}/collaborators/${editingMember.collaborator_id}`,
          {
            role: formData.role,
            pourcentage_benefices: parseFloat(formData.pourcentage_benefices) || 0,
            salaire_fixe: parseFloat(formData.salaire_fixe) || 0,
            notes: formData.notes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Création d'un nouveau collaborateur financier pour ce membre
        await axios.post(
          `${API_URL}/project-finances/${projectId}/collaborators`,
          {
            user_id: editingMember.user_id || editingMember.id,
            role: formData.role,
            pourcentage_benefices: parseFloat(formData.pourcentage_benefices) || 0,
            salaire_fixe: parseFloat(formData.salaire_fixe) || 0,
            date_debut: new Date().toISOString().split('T')[0],
            notes: formData.notes
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      setEditingMember(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);
  };

  const roleLabels = {
    auteur: { label: 'Auteur', color: 'bg-yellow-100 text-yellow-700', icon: '👑' },
    associe: { label: 'Associé', color: 'bg-purple-100 text-purple-700', icon: '🤝' },
    investisseur: { label: 'Investisseur', color: 'bg-green-100 text-green-700', icon: '💰' },
    employe: { label: 'Employé', color: 'bg-blue-100 text-blue-700', icon: '👔' },
    prestataire: { label: 'Prestataire', color: 'bg-orange-100 text-orange-700', icon: '🛠️' }
  };

  // Labels pour les rôles sur le projet (acteur, technicien, etc.)
  const projectRoleLabels = {
    acteur: { label: 'Acteur', icon: '🎭', color: 'text-pink-600 bg-pink-50' },
    figurant: { label: 'Figurant', icon: '👥', color: 'text-gray-600 bg-gray-100' },
    technicien: { label: 'Technicien', icon: '🔧', color: 'text-blue-600 bg-blue-50' },
    realisateur: { label: 'Réalisateur', icon: '🎬', color: 'text-purple-600 bg-purple-50' },
    scenariste: { label: 'Scénariste', icon: '📝', color: 'text-indigo-600 bg-indigo-50' },
    producteur: { label: 'Producteur', icon: '💼', color: 'text-amber-600 bg-amber-50' },
    autre: { label: 'Autre', icon: '📋', color: 'text-gray-600 bg-gray-100' }
  };

  // 3% revient à la plateforme DJAMANN
  const platformShare = 3;
  // Calcul du pourcentage restant pour l'auteur (après plateforme et collaborateurs)
  const authorShare = Math.max(0, 100 - platformShare - totalPourcentage);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Link to={`/projects/${projectId}/finances`} className="text-indigo-200 hover:text-white text-sm mb-2 inline-flex items-center gap-1">
            ← Retour au dashboard
          </Link>
          <h1 className="text-3xl font-bold">👥 Répartition des Parts</h1>
          <p className="text-indigo-200 mt-1">Gérez les parts de bénéfices des membres du projet</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Link to={`/projects/${projectId}/finances`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            📊 Dashboard
          </Link>
          <Link to={`/projects/${projectId}/finances/collaborators`} className="px-4 py-2 bg-indigo-600 text-white rounded-xl font-medium">
            👥 Collaborateurs
          </Link>
          <Link to={`/projects/${projectId}/finances/payments`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            💳 Paiements
          </Link>
          <Link to={`/projects/${projectId}/finances/reports`} className="px-4 py-2 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 shadow-sm">
            📈 Bilans
          </Link>
        </div>

        {/* Summary Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900">Répartition des bénéfices</h3>
              <p className="text-gray-500 mt-1">
                <span className="text-2xl font-bold text-indigo-600">{totalPourcentage.toFixed(1)}%</span> attribués aux collaborateurs
                <span className="text-sm ml-2">(+ {platformShare}% plateforme)</span>
              </p>
              <div className="w-full max-w-md bg-gray-200 rounded-full h-4 mt-3 overflow-hidden">
                <div className="h-full flex">
                  <div 
                    className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-500" 
                    style={{ width: `${platformShare}%` }}
                    title={`Plateforme DJAMANN: ${platformShare}%`}
                  ></div>
                  <div 
                    className="h-full bg-yellow-400 transition-all duration-500" 
                    style={{ width: `${authorShare}%` }}
                    title={`Auteur: ${authorShare.toFixed(1)}%`}
                  ></div>
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500" 
                    style={{ width: `${totalPourcentage}%` }}
                    title={`Collaborateurs: ${totalPourcentage.toFixed(1)}%`}
                  ></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-2 text-sm">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-teal-500 rounded"></span>
                  🏢 Plateforme: {platformShare}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-yellow-400 rounded"></span>
                  👑 Auteur: {authorShare.toFixed(1)}%
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-indigo-500 rounded"></span>
                  👥 Équipe: {totalPourcentage.toFixed(1)}%
                </span>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-500">Membres de l'équipe</p>
              <p className="text-3xl font-bold text-gray-900">{teamMembers.length}</p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-blue-800 text-sm">
            <span className="font-bold">ℹ️ Info:</span> Les membres affichés ici sont automatiquement issus de l'équipe du projet. 
            Vous pouvez modifier leurs parts de bénéfices en cliquant sur le bouton ✏️
          </p>
          <p className="text-blue-700 text-sm mt-2">
            <span className="font-bold">🏢 Plateforme DJAMANN:</span> 3% des bénéfices sont automatiquement prélevés pour le fonctionnement de la plateforme.
          </p>
        </div>

        {/* Team Members Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : teamMembers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member, idx) => (
              <div 
                key={member.user_id || member.id || idx} 
                className={`bg-white rounded-2xl shadow-sm overflow-hidden ${member.is_author ? 'ring-2 ring-yellow-400' : ''}`}
              >
                <div className={`h-2 ${member.is_author ? 'bg-yellow-400' : 'bg-indigo-500'}`}></div>
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold ${member.is_author ? 'bg-gradient-to-br from-yellow-400 to-orange-500' : 'bg-gradient-to-br from-indigo-500 to-purple-500'}`}>
                        {member.is_author ? '👑' : (member.nom?.[0]?.toUpperCase() || 'U')}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{member.nom}</h3>
                        <p className="text-sm text-gray-500">{member.email}</p>
                        {/* Rôle sur le projet (acteur, technicien, etc.) */}
                        {member.role_projet && (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium mt-1 px-2 py-0.5 rounded-full ${projectRoleLabels[member.role_projet]?.color || 'text-gray-600 bg-gray-100'}`}>
                            {projectRoleLabels[member.role_projet]?.icon || '📋'} {projectRoleLabels[member.role_projet]?.label || member.role_projet}
                          </span>
                        )}
                        {member.is_author && !member.role_projet && (
                          <p className="text-xs text-yellow-600 font-medium mt-1">👑 Créateur du projet</p>
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => handleEditParts(member)}
                        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="Modifier les parts"
                      >
                        ✏️
                      </button>
                    )}
                  </div>

                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${roleLabels[member.role_finance]?.color || 'bg-gray-100 text-gray-700'}`}>
                        {roleLabels[member.role_finance]?.icon || '👤'} {roleLabels[member.role_finance]?.label || 'Membre'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500">% Bénéfices</p>
                        <p className={`text-2xl font-bold ${member.is_author ? 'text-yellow-600' : 'text-indigo-600'}`}>
                          {member.is_author ? authorShare.toFixed(1) : member.pourcentage_benefices}%
                        </p>
                      </div>
                      {parseFloat(member.salaire_fixe) > 0 && (
                        <div>
                          <p className="text-xs text-gray-500">Salaire fixe</p>
                          <p className="text-lg font-semibold text-gray-800">{formatMoney(member.salaire_fixe)}/mois</p>
                        </div>
                      )}
                    </div>

                    {parseFloat(member.total_paye) > 0 && (
                      <div className="text-sm text-gray-500 pt-3 border-t border-gray-100">
                        <span className="font-medium text-green-600">Total perçu: {formatMoney(member.total_paye)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <span className="text-6xl">👥</span>
            <h3 className="text-xl font-bold text-gray-900 mt-4">Aucun membre dans l'équipe</h3>
            <p className="text-gray-500 mt-2">Les membres du projet apparaîtront ici automatiquement une fois ajoutés à l'équipe.</p>
            <Link 
              to={`/projects/${projectId}`}
              className="mt-6 inline-block px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors"
            >
              Voir le projet
            </Link>
          </div>
        )}
      </div>

      {/* Modal Edition Parts */}
      {showModal && editingMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-xl font-bold text-gray-900">✏️ Modifier les parts</h3>
              <p className="text-gray-500 mt-1">{editingMember.nom}</p>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Rôle financier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rôle financier</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(roleLabels).filter(([key]) => key !== 'auteur').map(([key, val]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, role: key }))}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        formData.role === key 
                          ? 'border-indigo-500 bg-indigo-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg">{val.icon}</span>
                      <p className="font-medium mt-1">{val.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Pourcentage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Part des bénéfices (%)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max={100 - totalPourcentage + (parseFloat(editingMember.pourcentage_benefices) || 0)}
                  value={formData.pourcentage_benefices}
                  onChange={(e) => setFormData(prev => ({ ...prev, pourcentage_benefices: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: 10"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum disponible: {(100 - totalPourcentage + (parseFloat(editingMember.pourcentage_benefices) || 0)).toFixed(1)}%
                </p>
              </div>

              {/* Salaire fixe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salaire fixe mensuel (XOF)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.salaire_fixe}
                  onChange={(e) => setFormData(prev => ({ ...prev, salaire_fixe: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder="0"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder="Notes optionnelles..."
                ></textarea>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingMember(null); }}
                  className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:shadow-lg"
                >
                  💾 Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCollaborators;
